import {
  TenantHaulierRegistrationData,
  UserHaulierRegistrationData,
} from '@zeus/common/lib/business/model/auth/haulier-registration-data'
import { AsyncUtil } from '@zeus/common/lib/util/async-util'
import { authService } from '@zeus/react-common/lib/business/service/auth-service'
import { ClientGuidService } from '@zeus/react-common/lib/business/service/client-guid-service'
import { jwtService } from '@zeus/react-common/lib/business/service/jwt-service'
import { accessTokenService } from '@zeus/react-common/lib/business/service/token/access-token-service'
import { refreshTokenService } from '@zeus/react-common/lib/business/service/token/refresh-token-service'
import React, { useContext, useRef, useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import arrowIcon from 'src/asset/image/arrow-back.svg'
import 'src/theme.scss'
import { SessionContext } from 'src/business/context/session-context'
import { AgreementsData } from 'src/business/model/sign-up-data'
import { signUpService } from 'src/business/service/sign-up-service'
import { HaulierAgreementForm } from 'src/component/haulier-sign-up/haulier-agreement-form'
import { HaulierTenantDetailsForm } from 'src/component/haulier-sign-up/haulier-tenant-details-form'
import { FullScreenLoader } from 'src/component/util/full-screen-loader'
import { toasterUtil } from 'src/util/toaster-util'

enum Steps {
  COMPANY_DETAILS = 'Company Details',
  AGREEMENTS = 'Agreements',
}

enum Actions {
  NEXT = 'Next',
  CONFIRM = 'Confirm',
}

export const HaulierDetailsStepper = (): React.ReactElement => {
  const [step, setStep] = useState<Steps>(Steps.COMPANY_DETAILS)
  const { invitationCode } = useContext(SessionContext)

  const [isLoading, setIsLoading] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const signUpData = location.state as UserHaulierRegistrationData

  const clientGuidService = new ClientGuidService()

  const tenantDataStepRef = useRef<{ submit: () => void }>(null)
  const agreementDataStepRef = useRef<{ submit: () => void }>(null)
  const tenantData = useRef<TenantHaulierRegistrationData | null>(null)

  const handleBackClick = (): void => {
    if (step === Steps.AGREEMENTS) {
      setStep(Steps.COMPANY_DETAILS)
    } else if (step === Steps.COMPANY_DETAILS) {
      navigate('/signup')
    }
  }

  const handleNextClick = async (): Promise<void> => {
    switch (step) {
      case Steps.COMPANY_DETAILS:
        tenantDataStepRef.current?.submit()
        break
      case Steps.AGREEMENTS:
        agreementDataStepRef.current?.submit()
        break
    }
  }

  const handleTenantFormSubmit = (data: TenantHaulierRegistrationData): void => {
    tenantData.current = data
    setStep(Steps.AGREEMENTS)
  }

  const handleAgreementsFormSubmit = (data: AgreementsData): void => {
    submitHaulier(data)
  }

  const getAccessTokenWithV1Data = async (): Promise<string> => {
    const { accessToken } = await authService.refreshToken()
    const { tenant, user } = await jwtService.decode(accessToken)
    if (tenant && user) return accessToken
    throw new Error()
  }

  const onSubmitSuccessful = async (params: { refreshToken: string; accessToken: string }): Promise<void> => {
    try {
      await refreshTokenService.store(params.refreshToken)
      await accessTokenService.store(params.accessToken)

      const { data: accessToken, success } = await new AsyncUtil().attemptCallbackWithDelay(getAccessTokenWithV1Data, 4)

      if (!success || !accessToken) {
        if (invitationCode) {
          navigate(`/v2/auth/login?invitation-code=${invitationCode}`)
        }
        throw new Error('TENANT_INFO_NOT_FOUND')
      }

      await accessTokenService.store(accessToken)

      if (invitationCode) {
        await authService.invitationCodeFulfill(invitationCode)
      }

      localStorage.removeItem('savedFormData')
      window.location.href = '/haulier/verification'
    } catch (e) {
      toasterUtil.error(
        `Something went wrong. If you're unable to log in with the information you just entered, please contact support.`,
      )
      window.location.href = '/auth/logout'
    } finally {
      setIsLoading(false)
    }
  }

  const submitHaulier = async (agreementsData: AgreementsData): Promise<void> => {
    setIsLoading(true)
    const clientGuid = await clientGuidService.getOrCreate()
    const response = await signUpService.registerHaulier({
      userData: { ...signUpData, clientGuid },
      tenantData: tenantData.current!,
      agreementsData,
    })
    onSubmitSuccessful(response.data[0])
  }

  const renderHeading = (): { heading: string; action: string } => {
    switch (step) {
      case Steps.COMPANY_DETAILS:
        return { heading: Steps.COMPANY_DETAILS, action: Actions.NEXT }
      case Steps.AGREEMENTS:
        return { heading: Steps.AGREEMENTS, action: Actions.CONFIRM }
      default:
        throw new Error(`Unexpected value of step: ${step}`)
    }
  }

  const { heading, action } = renderHeading()

  return (
    <div className="stepper-wrapper pt-lg-5 background-gray">
      {isLoading && <FullScreenLoader />}
      <Row>
        <Col xs={12} md={2}>
          <Button
            onClick={handleBackClick}
            variant="link"
            className="back-button flex align-item-center font-14 color-dark-blue mt-2"
          >
            <img className="me-2" alt="back icon" src={arrowIcon} />
            Back
          </Button>
        </Col>
        <Col xs={12} md={8}>
          <Row className="pb-5">
            <Col xs={12} sm={6}>
              <b className="font-36 color-dark-blue">{heading}</b>
            </Col>
            <Col xs={12} sm={6} className="text-end">
              <Button onClick={handleNextClick} color="primary" size="lg" className="continue-button">
                {action}
              </Button>
            </Col>
          </Row>
          <Row className="signup-section-heading">
            {step === Steps.COMPANY_DETAILS && (
              <HaulierTenantDetailsForm
                onSubmit={handleTenantFormSubmit}
                defaultValues={tenantData.current}
                ref={tenantDataStepRef}
              />
            )}
            {step === Steps.AGREEMENTS && (
              <HaulierAgreementForm ref={agreementDataStepRef} onSubmit={handleAgreementsFormSubmit} />
            )}
          </Row>
        </Col>
        <Col xs={12} md={2}></Col>
      </Row>
    </div>
  )
}
